import { useEffect, useState } from "react";
import { Button } from "../components/Button";
import { Alert } from "../components/Card";
import { Field, Input } from "../components/Field";
import { FormSplit, StatCard } from "../components/Page";
import { tx } from "../i18n";

/* ══ 연결 설정 › dbix 절 — 채택 시안 A안(Figma 10-3 아래쪽) ══
   사용이력을 보내고(수집 토큰) 읽는(조회 토큰) 연결 하나. ola-mops 절과 같은 화면의 다음 절이다 —
   사람이 「바깥 서비스에 어떻게 붙나」를 한 화면에서 찾는다(docs/decisions/usage-history.md ④).

   **판정은 서버가 한다** — 연결 시험의 갈래(`kind`)·뒤처진 건수·마지막 실패는 `crates/api/src/dbix.rs` 가
   준 사실이고, 화면은 그것을 문장으로 옮길 뿐이다. 토큰은 화면에 안 내려온다(`…TokenSet` 만). */

const API = import.meta.env.VITE_B3XIN_API_URL ?? "/api";

type Conn = { baseUrl: string; install: string; ingestTokenSet: boolean; queryTokenSet: boolean };
type Forward = {
  configured: boolean;
  missing: string[];
  cursor: number;
  behind: number;
  lastError: null | { kind: string; status: number | null; reasons: string[]; at: string };
  lastSentAt: string | null;
};
type Side = { kind: string; status?: number; detail?: string | null };
type TestResult = { kind?: string; ingest?: Side; query?: Side };

const GOOD = new Set(["ok"]);

function sideText(s: Side): string {
  const key = `dbix.result.${s.kind}`;
  const t = tx(key as "dbix.result.ok");
  return t === key ? s.kind : t;
}

export function DbixSection() {
  const [conn, setConn] = useState<Conn | null>(null);
  const [fwd, setFwd] = useState<Forward | null>(null);
  const [baseUrl, setBaseUrl] = useState("");
  const [install, setInstall] = useState("");
  const [ingest, setIngest] = useState("");
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetArmed, setResetArmed] = useState(false);
  const [note, setNote] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  function readForward() {
    fetch(`${API}/admin/dbix/forward`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setFwd)
      .catch((e: Error) => setLoadErr(e.message));
  }

  useEffect(() => {
    fetch(`${API}/admin/dbix/connection`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((c: Conn) => {
        setConn(c);
        setBaseUrl(c.baseUrl);
        setInstall(c.install);
      })
      .catch((e: Error) => setLoadErr(e.message));
    readForward();
  }, []);

  async function test() {
    setTesting(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/admin/dbix/connection/test`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        // 저장 없이 지금 칸의 값으로 시험한다 — 토큰을 안 적었으면 서버가 저장된 것을 쓴다.
        body: JSON.stringify({ baseUrl, ingestToken: ingest || undefined, queryToken: query || undefined }),
      });
      setResult(await r.json());
    } catch (e) {
      setResult({ ingest: { kind: "unreachable", detail: (e as Error).message }, query: { kind: "unreachable" } });
    } finally {
      setTesting(false);
    }
  }

  async function save() {
    setSaving(true);
    setNote(null);
    try {
      const r = await fetch(`${API}/admin/dbix/connection`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        // 토큰은 적었을 때만 보낸다 — 안 보내면 서버가 그대로 둔다(주소만 고쳐도 토큰이 안 날아간다).
        body: JSON.stringify({ baseUrl, install, ingestToken: ingest || undefined, queryToken: query || undefined }),
      });
      const v = await r.json().catch(() => null);
      // 400 은 서버가 무엇이 틀렸는지 말한다(ASCII 가 아닌 토큰) — 그 문장을 그대로 보인다.
      if (!r.ok) throw new Error(v?.message ?? `HTTP ${r.status}`);
      setConn(v);
      setBaseUrl(v.baseUrl);
      setInstall(v.install);
      setIngest("");
      setQuery("");
      setNote({ tone: "success", text: tx("dbix.saved") });
      readForward();
    } catch (e) {
      setNote({ tone: "error", text: tx("dbix.saveFailed", { err: (e as Error).message }) });
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    // 두 번 누른다 — 처음부터 다시 보내는 것은 되돌릴 일은 아니지만(dbix 가 접는다) 시간이 드는 일이다.
    if (!resetArmed) {
      setResetArmed(true);
      return;
    }
    setResetArmed(false);
    const r = await fetch(`${API}/admin/dbix/forward/reset`, { method: "POST" });
    if (r.ok) setFwd(await r.json());
  }

  const unknown = tx(loadErr ? "conn.stat.unknown" : "common.state.loading");
  const missing = (fwd?.missing ?? []).map((m) => tx(`dbix.missing.${m}` as "dbix.missing.baseUrl")).join(" · ");
  const rows = [
    {
      label: tx("dbix.stat.push"),
      value: fwd ? (fwd.configured ? `● ${tx("dbix.stat.pushOk")}` : `○ ${tx("dbix.stat.pushOff", { missing })}`) : unknown,
    },
    { label: tx("dbix.stat.behind"), value: fwd ? tx("dbix.stat.behindN", { n: fwd.behind }) : unknown },
    { label: tx("dbix.stat.lastSent"), value: fwd ? (fwd.lastSentAt ? new Date(fwd.lastSentAt).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" }) : tx("dbix.stat.never")) : unknown },
    {
      label: tx("dbix.stat.lastError"),
      value: fwd
        ? fwd.lastError
          ? `✕ ${tx(`dbix.fail.${fwd.lastError.kind}` as "dbix.fail.unreachable")}${fwd.lastError.reasons.length ? ` (${fwd.lastError.reasons.join(", ")})` : ""}`
          : tx("dbix.stat.never")
        : unknown,
    },
  ];

  return (
    <section className="dbix" aria-labelledby="dbix-title">
      <h2 id="dbix-title" className="b-card-title">
        {tx("dbix.title")}
      </h2>
      <p className="caption">{tx("dbix.lead")}</p>
      {loadErr ? <Alert tone="error">{tx("dbix.loadFailed", { err: loadErr })}</Alert> : null}
      <FormSplit
        aside={
          <>
            <StatCard rows={rows} />
            <div className="dbix__reset">
              <Button onClick={reset} disabled={!fwd}>
                {resetArmed ? tx("dbix.resetConfirm") : tx("dbix.reset")}
              </Button>
              <p className="caption">{tx("dbix.resetHint")}</p>
            </div>
          </>
        }
      >
        <div className="conn__form">
          <Field label={tx("dbix.address")} hint={tx("dbix.addressHint")}>
            {(id) => <Input id={id} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://dbix.mops.internal" inputMode="url" />}
          </Field>
          <Field label={tx("dbix.install")} hint={tx("dbix.installHint")}>
            {(id) => <Input id={id} value={install} onChange={(e) => setInstall(e.target.value)} placeholder="in-prod" />}
          </Field>
          <Field label={tx("dbix.ingestToken")} hint={tx("dbix.ingestHint")}>
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="off"
                value={ingest}
                onChange={(e) => setIngest(e.target.value)}
                placeholder={tx(conn?.ingestTokenSet ? "dbix.tokenSetPh" : "dbix.tokenNonePh")}
              />
            )}
          </Field>
          <Field label={tx("dbix.queryToken")} hint={tx("dbix.queryHint")}>
            {(id) => (
              <Input
                id={id}
                type="password"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tx(conn?.queryTokenSet ? "dbix.tokenSetPh" : "dbix.tokenNonePh")}
              />
            )}
          </Field>
          <div className="dbix__btns">
            <Button variant="primary" size="lg" onClick={save} loading={saving}>
              {tx("common.action.save")}
            </Button>
            <Button size="lg" onClick={test} loading={testing}>
              {tx("dbix.test")}
            </Button>
          </div>
          {note ? <Alert tone={note.tone}>{note.text}</Alert> : null}
          {result?.kind ? <Alert tone="info">{sideText({ kind: result.kind })}</Alert> : null}
          {result?.ingest && result.query
            ? (["ingest", "query"] as const).map((side) => {
                const s = result[side]!;
                return (
                  <Alert key={side} tone={GOOD.has(s.kind) ? "success" : "error"}>
                    <b>{tx(`dbix.side.${side}`)}</b> — {sideText(s)}
                    {s.status ? ` (${s.status})` : ""}
                    {s.detail ? ` [${s.detail}]` : ""}
                  </Alert>
                );
              })
            : null}
        </div>
      </FormSplit>
    </section>
  );
}
